import { useCallback } from "react";
import { NavigateOptions, useNavigate } from "react-router-dom";

import { createURLSearchParams } from "@hooks/useFilters";

import { ICompanyFilters } from "@interfaces/filter.type";

/**
 * Custom hook to navigate with search parameters we have defined in ICompanyFilters interface.
 * This hook is extended from useNavigate hook from react-router-dom.
 *
 * @returns A callback function that takes up to three parameters:
 * - `url` (string): The URL to navigate to.
 * - `searchParams` (ICompanyFilters | undefined): An optional object containing search parameters to append to the URL.
 * - `options` (NavigateOptions | undefined): An optional object containing navigation options.
 *
 * The callback constructs a URL with the provided search parameters, and then navigates to that URL using the provided navigation options.
 */
const useNavigateWithSearchParameters = () => {
  const navigate = useNavigate();

  return useCallback(
    (url: string, searchParameters?: ICompanyFilters, options?: NavigateOptions) => {
      const search = searchParameters && createURLSearchParams(searchParameters);
      let fullUrl = url;
      if (search) {
        fullUrl += "?" + search;
      }
      navigate(fullUrl, options);
    },
    [navigate],
  );
};

export default useNavigateWithSearchParameters;
